import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Plus, Pencil, Trash2, Smartphone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getApplications, createApplication, updateApplication, deleteApplication } from '../../lib/api';
import ImageUpload from '../../components/common/ImageUpload';

const AdminApplications = () => {
  const { t } = useLanguage();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ 
    name_ar: '', name_en: '', 
    description_ar: '', description_en: '', 
    icon_url: '',
    screenshots: [], 
    app_store_url: '', play_store_url: '', 
    order: 0, is_active: true 
  });

  const loadData = async () => { 
    try { 
      const res = await getApplications(); 
      setApps(res.data); 
    } catch (error) { 
      toast.error(t('خطأ في تحميل البيانات', 'Error loading data')); 
    } finally { 
      setLoading(false); 
    } 
  };
  
  useEffect(() => { loadData(); }, []);

  const openCreateDialog = () => {
    setSelectedApp(null);
    setFormData({ 
      name_ar: '', name_en: '', 
      description_ar: '', description_en: '', 
      icon_url: '',
      screenshots: [], 
      app_store_url: '', play_store_url: '', 
      order: apps.length, is_active: true 
    });
    setDialogOpen(true);
  };

  const openEditDialog = (app) => {
    setSelectedApp(app);
    setFormData({ 
      name_ar: app.name_ar, 
      name_en: app.name_en, 
      description_ar: app.description_ar, 
      description_en: app.description_en, 
      icon_url: app.icon_url || '',
      screenshots: app.screenshots || [], 
      app_store_url: app.app_store_url || '', 
      play_store_url: app.play_store_url || '', 
      order: app.order, 
      is_active: app.is_active 
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name_ar || !formData.name_en) { 
      toast.error(t('يرجى ملء جميع الحقول المطلوبة', 'Please fill all required fields')); 
      return; 
    }
    setSubmitting(true);
    try {
      if (selectedApp) { 
        await updateApplication(selectedApp.id, formData); 
        toast.success(t('تم التحديث بنجاح', 'Updated successfully')); 
      } else { 
        await createApplication(formData); 
        toast.success(t('تم الإنشاء بنجاح', 'Created successfully')); 
      }
      setDialogOpen(false); 
      loadData();
    } catch (error) { 
      toast.error(t('حدث خطأ', 'An error occurred')); 
    } finally { 
      setSubmitting(false); 
    }
  };

  const handleDelete = async () => { 
    try { 
      await deleteApplication(selectedApp.id); 
      toast.success(t('تم الحذف بنجاح', 'Deleted successfully')); 
      setDeleteDialogOpen(false); 
      loadData(); 
    } catch (error) { 
      toast.error(t('حدث خطأ', 'Error')); 
    } 
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-raad-rose border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div data-testid="admin-applications">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('التطبيقات', 'Applications')}</h1>
          <p className="text-gray-400">{t('إدارة تطبيقات الشركة', 'Manage company applications')}</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-gradient-raad" data-testid="add-app-btn">
          <Plus className="w-4 h-4 me-2" />
          {t('إضافة تطبيق', 'Add Application')}
        </Button>
      </div>

      {apps.length === 0 ? (
        <Card className="bg-raad-card border-white/10">
          <CardContent className="py-12 text-center">
            <p className="text-gray-400">{t('لا توجد تطبيقات', 'No applications yet')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {apps.map((app) => (
            <Card key={app.id} className="bg-raad-card border-white/10">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  {app.icon_url ? (
                    <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/5">
                      <img 
                        src={app.icon_url} 
                        alt={t(app.name_ar, app.name_en)}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-gradient-raad flex items-center justify-center">
                      <Smartphone className="w-7 h-7 text-white" />
                    </div>
                  )}
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(app)} className="text-gray-400 hover:text-white">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => { setSelectedApp(app); setDeleteDialogOpen(true); }} className="text-gray-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <h3 className="text-white font-semibold mb-1">{t(app.name_ar, app.name_en)}</h3>
                <p className="text-gray-400 text-sm line-clamp-2 mb-4">{t(app.description_ar, app.description_en)}</p>
                <span className={`px-2 py-1 rounded-full text-xs ${app.is_active ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20 text-gray-500'}`}>
                  {app.is_active ? t('نشط', 'Active') : t('غير نشط', 'Inactive')}
                </span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-raad-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {selectedApp ? t('تعديل التطبيق', 'Edit App') : t('إضافة تطبيق', 'Add App')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">{t('الاسم (عربي)', 'Name (Arabic)')} *</Label>
                <Input value={formData.name_ar} onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" dir="rtl" />
              </div>
              <div>
                <Label className="text-gray-300">{t('الاسم (إنجليزي)', 'Name (English)')} *</Label>
                <Input value={formData.name_en} onChange={(e) => setFormData({ ...formData, name_en: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" dir="ltr" />
              </div>
            </div>

            {/* App Icon Upload */}
            <div>
              <ImageUpload
                label={t('أيقونة التطبيق', 'App Icon')}
                value={formData.icon_url}
                onChange={(url) => setFormData({ ...formData, icon_url: url })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">{t('الوصف (عربي)', 'Description (Arabic)')}</Label>
                <Textarea value={formData.description_ar} onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" dir="rtl" rows={3} />
              </div>
              <div>
                <Label className="text-gray-300">{t('الوصف (إنجليزي)', 'Description (English)')}</Label>
                <Textarea value={formData.description_en} onChange={(e) => setFormData({ ...formData, description_en: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" dir="ltr" rows={3} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">App Store URL</Label>
                <Input value={formData.app_store_url} onChange={(e) => setFormData({ ...formData, app_store_url: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" />
              </div>
              <div>
                <Label className="text-gray-300">Google Play URL</Label>
                <Input value={formData.play_store_url} onChange={(e) => setFormData({ ...formData, play_store_url: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">{t('صور التطبيق', 'Screenshots')}</Label>
              <Textarea value={formData.screenshots.join('\n')} onChange={(e) => setFormData({ ...formData, screenshots: e.target.value.split('\n').filter(s => s.trim()) })} className="bg-white/5 border-white/10 text-white mt-1" rows={3} placeholder={t('رابط واحد في كل سطر', 'One URL per line')} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={formData.is_active} onCheckedChange={(c) => setFormData({ ...formData, is_active: c })} />
              <Label className="text-gray-300">{t('نشط', 'Active')}</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>{t('إلغاء', 'Cancel')}</Button>
              <Button type="submit" disabled={submitting} className="bg-gradient-raad">
                {submitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : selectedApp ? t('تحديث', 'Update') : t('إنشاء', 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-raad-card border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{t('تأكيد الحذف', 'Confirm Delete')}</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">{t('هل أنت متأكد؟', 'Are you sure?')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">{t('إلغاء', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">{t('حذف', 'Delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminApplications;
